import { Link } from "react-router-dom";
import { ShieldCheck, Target, Eye, Award, ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { SectionHeading, QuotationCTA } from "@/components/site/Shared";
import { useSEO } from "@/hooks/useSEO";
import { IMAGES } from "@/data/catalog";

export default function About() {
  useSEO({
    title: "About Us — Renovation & Engineering Contractor Singapore",
    description:
      "Learn about WAAN METAL PTE. LTD., an HDB-registered multi-disciplinary renovation, fit-out, interior design and metal fabrication contractor based in Singapore.",
  });
  return (
    <Layout>
      <section className="relative bg-[#0A1128] py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0"><img src={IMAGES.metalFab} alt="" className="w-full h-full object-cover opacity-20" /><div className="absolute inset-0 bg-[#0A1128]/85" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow mb-3">About WAAN METAL</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight max-w-3xl">An established contractor from homes to heavy industry.</h1>
          <p className="mt-5 text-slate-300 max-w-2xl text-lg">We combine renovation craftsmanship, interior design and in-house metal fabrication under one accountable team.</p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow mb-3">Our Story</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Engineering discipline meets design capability.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              WAAN METAL PTE. LTD. is a Singapore-based contractor providing metal works, renovation contracting and
              interior design across residential, commercial and industrial sectors. As an HDB-registered renovation
              contractor, we deliver permitted residential renovation works while bringing the same engineering rigour
              to commercial fit-outs and industrial facilities.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Our in-house metal fabrication workshop lets us control quality and timelines on everything from gates and
              grilles to steel platforms and architectural metalwork.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B87333]/10 border border-[#B87333]/25 text-[#B87333] text-sm font-semibold">
              <ShieldCheck size={16} /> HDB-Registered Renovation Contractor
            </div>
          </div>
          <img src={IMAGES.commercial1} alt="Our work" className="rounded-2xl w-full h-[420px] object-cover" />
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="What Drives Us" title="Mission, vision and values" center />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: "Our Mission", desc: "To be Singapore's most trusted multi-disciplinary contractor, delivering quality work with transparent pricing." },
              { icon: Eye, title: "Our Vision", desc: "Spaces and structures that balance design, function and durability — for homes, businesses and facilities." },
              { icon: Award, title: "Our Values", desc: "Integrity, craftsmanship, compliance and accountability on every project we undertake." },
            ].map((v) => (
              <div key={v.title} className="p-8 rounded-2xl bg-white border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#B87333] to-[#D97706] flex items-center justify-center text-[#0A1128] mb-5"><v.icon size={24} /></div>
                <h3 className="text-xl font-semibold text-slate-900">{v.title}</h3>
                <p className="mt-2.5 text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 rounded-2xl border border-slate-200 bg-slate-50 p-8">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck size={18} className="text-[#B87333]" /> Compliance Note</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            HDB registration applies to permitted renovation works only and does not authorise every type of work.
            Structural, electrical, fire-safety and other regulated works are carried out subject to the applicable HDB,
            building, fire-safety and other regulatory approvals, by appropriately qualified and licensed professionals.
            Additional accreditations are displayed only where independently confirmed.
          </p>
        </div>
      </section>

      <QuotationCTA />
    </Layout>
  );
}
